import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useDispatch, useSelector } from 'react-redux';
import 'react-contexify/ReactContexify.css';
import { Button, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import { formatDateWithTime } from '../helpers/formattedDateTime';
import { deleteAdminUserLabelById, getAdminUserLabelList, getAllUserLabelList, getSharedUserLabelList } from '../redux/actions/userActions';
import { isSuperUser } from '../redux/selectors/userSelector';
import { Delete, Edit, Label, Share } from '@mui/icons-material';
import AddLabelModal from '../models/AddLabelModal';
import TextFieldComponent from '../components/TextFieldComponent';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { setCandidateFilters } from '../redux/actions/candidateActions';
import ShareLabelModal from '../models/ShareLabelModal';
import StickyTableContainer from '../components/StickyTableContainer';

const ViewUserLabels = () => {
  const [value, setValue] = React.useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [screenChangedForAllLabel, setScreenChangedForAllLabel] = useState(false);
  const [screenChangedForSharedLabel, setScreenChangedForSharedLabel] = useState(false);
  const [isAddLabelModalVisible, setIsAddLabelModalVisible] = useState(false);
  const [isLabelDeleteModalVisible, setIsLabelDeleteModalVisible] = useState(false);
  const [isShareLabelModalVisible, setIsShareLabelModalVisible] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);

  const candidateFilters = useSelector((state) => state.candidate.candidateFilters);

  const [selfLabelQueryParams, setSelfLabelQueryParams] = useState({
    pageNumber: 0
  });

  const [allLabelQueryParams, setAllLabelQueryParams] = useState({
    pageNumber: 0,
    searchText: ''
  });

  const [sharedLabelQueryParams, setSharedLabelQueryParams] = useState({
    pageNumber: 0,
    shared: true
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const superUser = useSelector(isSuperUser);
  const userDetails = useSelector((state) => state.user.userDetails);

  const labelsResponse = useSelector((state) => state.user.adminUserLabels);
  const labels = labelsResponse?.data;

  const allLabelsResponse = useSelector((state) => state.user.allUserLabels);
  const allLabels = allLabelsResponse?.data;

  const sharedLabelsResponse = useSelector((state) => state.user.sharedUserLabels);
  const sharedLabels = sharedLabelsResponse?.data;

  // get the totalRecords for selected tab based on value state
  const totalRecords = (value == 0 ?
    labelsResponse?.metadata?.totalRecords :
    (value == 1) ?
      allLabelsResponse?.metadata?.totalRecords :
      sharedLabelsResponse?.metadata?.totalRecords
  );

  // get the totalPages for selected tab based on value state
  const totalPages = (value == 0 ?
    labelsResponse?.metadata?.totalPages :
    (value == 1) ?
      allLabelsResponse?.metadata?.totalPages :
      sharedLabelsResponse?.metadata?.totalPages
  );

  useEffect(() => {
    let paramJson = {
      ...selfLabelQueryParams,
      "adminUserId": userDetails?.adminUserId
    }
    dispatch(getAdminUserLabelList(paramJson));
  }, [dispatch, selfLabelQueryParams]);

  const allLabelTabDidMount = useRef(true);
  useEffect(() => {
    setScreenChangedForAllLabel(true);
    if (allLabelTabDidMount.current) {
      allLabelTabDidMount.current = false;
      return;
    }
    dispatch(getAllUserLabelList(allLabelQueryParams));
  }, [allLabelQueryParams]);

  const sharedLabelTabDidMount = useRef(true);
  useEffect(() => {
    setScreenChangedForSharedLabel(true);
    if (sharedLabelTabDidMount.current) {
      sharedLabelTabDidMount.current = false;
      return;
    }
    let paramJson = {
      ...sharedLabelQueryParams,
      "adminUserId": userDetails?.adminUserId
    }
    dispatch(getSharedUserLabelList(paramJson));
  }, [sharedLabelQueryParams]);


  const handlePageChange = (event, page) => {
    if (value == 0) {
      setSelfLabelQueryParams((prevParams) => ({ ...prevParams, pageNumber: page - 1 }));
    } else if (value == 1) {
      setAllLabelQueryParams((prevParams) => ({ ...prevParams, pageNumber: page - 1 }));
    } else {
      setSharedLabelQueryParams((prevParams) => ({ ...prevParams, pageNumber: page - 1 }));
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue == 1) {
      if (screenChangedForAllLabel || allLabels == null || allLabels?.length == 0) {
        setScreenChangedForAllLabel(false);
        dispatch(getAllUserLabelList(allLabelQueryParams));
      }
    } else if (newValue == 2) {
      if (screenChangedForSharedLabel || sharedLabels == null || sharedLabels?.length == 0) {
        setScreenChangedForSharedLabel(false);
        let paramJson = {
          ...sharedLabelQueryParams,
          "adminUserId": userDetails?.adminUserId
        }
        dispatch(getSharedUserLabelList(paramJson));
      }
    }
  };

  const handleSearch = (text) => {
    setSearchTerm(text);
    clearTimeout(typingTimeout); // Clear the previous timeout
    const timeout = setTimeout(() => {
      setAllLabelQueryParams((prevParams) => ({ ...prevParams, searchText: text, pageNumber: 0 }));
      setSelfLabelQueryParams((prevParams) => ({ ...prevParams, searchText: text, pageNumber: 0 }));
      setSharedLabelQueryParams((prevParams) => ({ ...prevParams, searchText: text, pageNumber: 0 }));
    }, 700);
    setTypingTimeout(timeout);
  };

  const onNewLabelAdded = () => {
    let paramJson = {
      ...selfLabelQueryParams,
      "adminUserId": userDetails?.adminUserId
    }
    dispatch(getAdminUserLabelList(paramJson));
    dispatch(getAllUserLabelList(allLabelQueryParams));
  }

  const handleAddLabel = () => {
    setSelectedLabel(null);
    setIsAddLabelModalVisible(true);
  }

  const handleCloseAddLabelModal = () => {
    setIsAddLabelModalVisible(false);
  }

  const handleEditClick = (label) => {
    setSelectedLabel(label);
    setIsAddLabelModalVisible(true);
  }

  const handleShareLabel = (label) => {
    setSelectedLabel(label);
    setIsShareLabelModalVisible(true);
  }

  const handleCloseShareLabelModal = () => {
    setIsShareLabelModalVisible(false);
    setSelectedLabel(null);
  }

  const handleDeleteClick = (id) => {
    setIsLabelDeleteModalVisible(true);
    setSelectedLabelId(id);
  };

  const handleConfirmationDialogClose = () => {
    setIsLabelDeleteModalVisible(false);
  };

  const handleConfirmationDialog = () => {
    setIsLabelDeleteModalVisible(false);
    setSelectedLabelId(null);
    dispatch(deleteAdminUserLabelById(selectedLabelId));
  };

  const handleSelectLabel = (labelId) => {
    let canFilters = { ...candidateFilters };
    canFilters['label'] = labelId;
    canFilters['isApplied'] = true;
    dispatch(setCandidateFilters(canFilters));
    navigate(`/app/candidates`);
  }

  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const SelfLabelView = () => {
    return (
      <>
        {labels?.length ? (
          <>
            <StickyTableContainer>
              <TableHead>
                <TableRow>
                  <TableCell style={styles.textBold}>Name</TableCell>
                  <TableCell style={styles.textBold}>Assigned User Count</TableCell>
                  <TableCell style={styles.textBold}>Create Date</TableCell>
                  <TableCell style={styles.textBold}>Update Date</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labels?.map((label, index) => (
                  <TableRow key={label.id}
                    onClick={() => setSelectedRow(index)}
                    onDoubleClick={() => handleSelectLabel(label.id)}
                    style={{
                      backgroundColor: selectedRow === index ? '#e0e0e0' : 'white',
                    }}
                  >
                    <TableCell style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                      <Label style={{ color: label.color, marginRight: '12px' }} />
                      {label.name}
                    </TableCell>
                    <TableCell>{label.assignedUserCount}</TableCell>
                    <TableCell>{label.createDate && formatDateWithTime(label.createDate)}</TableCell>
                    <TableCell>{label.updateDate && formatDateWithTime(label.updateDate)}</TableCell>
                    <TableCell style={{ padding: '4px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', columnGap: '3px' }}>
                        <Tooltip title="Share">
                          <IconButton onClick={() => handleShareLabel(label)}>
                            <Share color='primary' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEditClick(label)}>
                            <Edit color='primary' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDeleteClick(label.id)}>
                            <Delete color='primary' />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </StickyTableContainer>

            <Grid container justifyContent="flex-end" style={{ marginTop: '16px' }}>
              <PaginationComponent
                pageNumber={selfLabelQueryParams.pageNumber + 1}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            </Grid>
          </>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <h1>No Labels found</h1>
          </div>
        )}
      </>
    )
  }

  const AllLabelView = () => {
    return (
      <>
        {allLabels?.length ? (
          <>
            <StickyTableContainer>
              <TableHead>
                <TableRow>
                  <TableCell style={styles.textBold}>Name</TableCell>
                  <TableCell style={styles.textBold}>Assigned User Count</TableCell>
                  <TableCell style={styles.textBold}>Created By</TableCell>
                  <TableCell style={styles.textBold}>Create Date</TableCell>
                  <TableCell style={styles.textBold}>Update Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allLabels?.map((label, index) => (
                  <TableRow key={label.id}
                    onClick={() => setSelectedRow(index)}
                    onDoubleClick={() => handleSelectLabel(label.id)}
                    style={{
                      backgroundColor: selectedRow === index ? '#e0e0e0' : 'white',
                    }}
                  >
                    <TableCell style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                      <Label style={{ color: label.color, marginRight: '12px' }} />
                      {label.name}
                    </TableCell>
                    <TableCell>{label.assignedUserCount}</TableCell>
                    <TableCell>{label.adminUserName}</TableCell>
                    <TableCell>{label.createDate && formatDateWithTime(label.createDate)}</TableCell>
                    <TableCell>{label.updateDate && formatDateWithTime(label.updateDate)}</TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </StickyTableContainer>

            <Grid container justifyContent="flex-end" style={{ marginTop: '16px' }}>
              <PaginationComponent
                pageNumber={allLabelQueryParams.pageNumber + 1}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            </Grid>
          </>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <h1>No Labels found</h1>
          </div>
        )}
      </>
    )
  }

  const SharedLabelView = () => {
    return (
      <>
        {sharedLabels?.length ? (
          <>
            <StickyTableContainer>
              <TableHead>
                <TableRow>
                  <TableCell style={styles.textBold}>Name</TableCell>
                  <TableCell style={styles.textBold}>Assigned User Count</TableCell>
                  <TableCell style={styles.textBold}>Shared By</TableCell>
                  <TableCell style={styles.textBold}>Create Date</TableCell>
                  <TableCell style={styles.textBold}>Update Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sharedLabels?.map((label, index) => (
                  <TableRow key={label.id}
                    onClick={() => setSelectedRow(index)}
                    onDoubleClick={() => handleSelectLabel(label.id)}
                    style={{
                      backgroundColor: selectedRow === index ? '#e0e0e0' : 'white',
                    }}
                  >
                    <TableCell style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                      <Label style={{ color: label.color, marginRight: '12px' }} />
                      {label.name}
                    </TableCell>
                    <TableCell>{label.assignedUserCount}</TableCell>
                    <TableCell>{label.adminUserName}</TableCell>
                    <TableCell>{label.createDate && formatDateWithTime(label.createDate)}</TableCell>
                    <TableCell>{label.updateDate && formatDateWithTime(label.updateDate)}</TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </StickyTableContainer>

            <Grid container justifyContent="flex-end" style={{ marginTop: '16px' }}>
              <PaginationComponent
                pageNumber={sharedLabelQueryParams.pageNumber + 1}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            </Grid>
          </>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <h1>No Shared Labels found</h1>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <HeaderComponent name="Labels" totalRecords={totalRecords} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 12 }} >

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Search Input on the Left Side */}
            <TextFieldComponent
              label="Search"
              type="search"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: '350px' }}
            />

            {/* Add Create Label button on the Right Side */}
            <Tooltip title="Create Label">
              <Button style={{ whiteSpace: 'nowrap' }} variant="contained" color="primary" onClick={handleAddLabel}>
                Create Label
              </Button>
            </Tooltip>
          </div>

          <div style={{ margin: '16px 0' }} />
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="My Labels" value={0} {...a11yProps(0)} />
            {superUser && <Tab label="All Labels" value={1} {...a11yProps(1)} />}
            <Tab label="Shared Labels" value={2} {...a11yProps(2)} />
          </Tabs>

          {value == 0 &&
            <div>
              {SelfLabelView()}
            </div>
          }

          {value == 1 &&
            <div>
              {AllLabelView()}
            </div>
          }

          {value == 2 &&
            <div>
              {SharedLabelView()}
            </div>
          }

        </Grid>

        {isAddLabelModalVisible &&
          <AddLabelModal
            open={true}
            onClose={handleCloseAddLabelModal}
            label={selectedLabel}
            onNewLabelAdded={onNewLabelAdded}
          />
        }

        {isLabelDeleteModalVisible === true && (
          <ConfirmationDialog
            open={true}
            onClose={handleConfirmationDialogClose}
            onConfirm={handleConfirmationDialog}
            message="Do you want to Delete?"
          />
        )}

        {isShareLabelModalVisible &&
          <ShareLabelModal
            open={true}
            onClose={handleCloseShareLabelModal}
            label={selectedLabel}
          />
        }

      </Grid>
    </>
  );
};

export default ViewUserLabels;

const styles = {
  textBold: {
    fontWeight: 'bold'
  }
}